import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, MapPin, Briefcase, DollarSign, Clock, 
  Building, ExternalLink, Loader2, Linkedin, User
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ApplicationModal from '../components/ApplicationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const fetchJob = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs/${jobId}`);
      
      if (response.ok) {
        const data = await response.json();
        setJob(data.job);
      } else {
        toast({ 
          title: 'Error', 
          description: 'Job not found', 
          variant: 'destructive' 
        });
        navigate('/jobs');
      }
    } catch (error) {
      console.error('Error fetching job:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to load job', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Google for Jobs compliant JobPosting structured data
  const generateJobPostingSchema = () => {
    if (!job) return '';

    const escapeHtml = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const toHtml = (s) => `<p>${escapeHtml(s).split(/\n{2,}/).join('</p><p>').replace(/\n/g, '<br>')}</p>`;

    let descriptionHtml = toHtml(job.description);
    if (job.requirements) {
      descriptionHtml += `<h3>Requirements</h3>${toHtml(job.requirements)}`;
    }
    if (job.skills && job.skills.length > 0) {
      descriptionHtml += `<p><strong>Required Skills:</strong> ${escapeHtml(job.skills.join(', '))}</p>`;
    }

    const employmentTypeMap = {
      full_time: 'FULL_TIME',
      part_time: 'PART_TIME',
      contract: 'CONTRACTOR',
      temporary: 'TEMPORARY',
      internship: 'INTERN'
    };

    const jobUrl = `${window.location.origin}/jobs/${job.job_id}`;

    const schema = {
      "@context": "https://schema.org/",
      "@type": "JobPosting",
      "title": job.title,
      "description": descriptionHtml,
      "identifier": {
        "@type": "PropertyValue",
        "name": job.company_name,
        "value": job.job_id
      },
      "datePosted": job.published_at || job.created_at,
      "hiringOrganization": {
        "@type": "Organization",
        "name": job.company_name
      },
      "url": jobUrl,
      "directApply": job.application_method === 'platform'
    };

    if (job.expires_at) schema.validThrough = job.expires_at;

    const employmentType = employmentTypeMap[job.job_type];
    if (employmentType) schema.employmentType = employmentType;

    if (job.work_location === 'remote') {
      schema.jobLocationType = "TELECOMMUTE";
      if (job.country) {
        schema.applicantLocationRequirements = { "@type": "Country", "name": job.country };
      }
    } else if (job.country) {
      schema.jobLocation = {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          ...(job.city && { "addressLocality": job.city }),
          ...(job.state && { "addressRegion": job.state }),
          "addressCountry": job.country
        }
      };
    }

    if (job.salary_min || job.salary_max) {
      schema.baseSalary = {
        "@type": "MonetaryAmount",
        "currency": job.salary_currency || "USD",
        "value": {
          "@type": "QuantitativeValue",
          ...(job.salary_min && { "minValue": job.salary_min }),
          ...(job.salary_max && { "maxValue": job.salary_max }),
          "unitText": "YEAR"
        }
      };
    }

    if (job.experience_level) {
      const experienceMonths = { entry: 0, mid: 24, senior: 60, executive: 120 };
      schema.experienceRequirements = {
        "@type": "OccupationalExperienceRequirements",
        "monthsOfExperience": experienceMonths[job.experience_level] ?? 0
      };
    }

    if (job.skills && job.skills.length > 0) {
      schema.skills = job.skills.join(', ');
    }

    return JSON.stringify(schema);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!job) {
    return null;
  }

  return (
    <>
      {/* Google for Jobs Structured Data */}
      {/* React 19 hoists title/meta/link to <head>; JSON-LD in body is valid for Google */}
      <title>{`${job.title} at ${job.company_name} | RecruiterIQ Jobs`}</title>
      <meta name="description" content={job.description?.substring(0, 160)} />
      <link rel="canonical" href={`${window.location.origin}/jobs/${job.job_id}`} />
      {job.status === 'active' && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: generateJobPostingSchema() }} />
      )}

      <div className="min-h-screen bg-slate-50 py-8 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <Button 
            onClick={() => navigate('/jobs')} 
            variant="outline" 
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Jobs
          </Button>

          {/* Job Header */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-slate-900 mb-3">{job.title}</h1>
                  
                  <div className="flex items-center gap-2 text-lg text-slate-700 mb-4">
                    <Building className="w-5 h-5" />
                    <span className="font-semibold">{job.company_name}</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-slate-600 mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {job.city ? `${job.city}, ` : ''}{job.state ? `${job.state}, ` : ''}{job.country}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      {job.job_type?.replace('_', ' ')}
                    </div>
                    
                    <Badge className="bg-blue-100 text-blue-700">
                      {job.work_location}
                    </Badge>
                    
                    {job.experience_level && (
                      <Badge variant="outline">
                        {job.experience_level} level
                      </Badge>
                    )}
                  </div>

                  {job.salary_min && (
                    <div className="flex items-center gap-2 text-lg font-semibold text-green-600 mb-4">
                      <DollarSign className="w-5 h-5" />
                      {job.salary_currency} {job.salary_min.toLocaleString()}
                      {job.salary_max && ` - ${job.salary_max.toLocaleString()}`} / year
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Clock className="w-4 h-4" />
                    Posted on {formatDate(job.published_at || job.created_at)}
                  </div>
                </div>

                {/* Apply Button */}
                <div className="flex flex-col gap-2 ml-6">
                  {job.application_method === 'external_url' ? (
                    <Button
                      onClick={() => window.open(job.application_url, '_blank')}
                      className="bg-blue-600 hover:bg-blue-700"
                      size="lg"
                    >
                      Apply on Company Site
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setShowApplicationModal(true)}
                      className="bg-blue-600 hover:bg-blue-700"
                      size="lg"
                    >
                      Apply Now
                    </Button>
                  )}
                  
                  {/* Posted By Info */}
                  {job.posted_by_name && (
                    <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-xs font-semibold text-slate-600 mb-2">Posted by</p>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-slate-900">{job.posted_by_name}</p>
                          {job.posted_by_linkedin_url && (
                            <a 
                              href={job.posted_by_linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 hover:underline mt-0.5"
                            >
                              <Linkedin className="w-3 h-3" />
                              View LinkedIn Profile
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Skills */}
              {job.skills && job.skills.length > 0 && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Required Skills:</p>
                  <div className="flex flex-wrap gap-2">
                    {job.skills.map((skill, idx) => (
                      <Badge key={idx} variant="secondary">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Job Description */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Job Description</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-slate max-w-none">
                <p className="whitespace-pre-wrap text-slate-700">{job.description}</p>
              </div>
            </CardContent>
          </Card>

          {/* Requirements */}
          {job.requirements && (
            <Card>
              <CardHeader>
                <CardTitle>Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap">{job.requirements}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Application Modal */}
        <ApplicationModal
          isOpen={showApplicationModal}
          onClose={() => setShowApplicationModal(false)}
          jobId={jobId}
          jobTitle={job?.title || ''}
          companyName={job?.company_name || ''}
        />
      </div>
    </>
  );
};

export default JobDetailPage;
